import qs from 'qs'
import { keysToSnake, keysToCamel } from '~~/app/utils/format'
import type { NitroFetchOptions, NitroFetchRequest } from 'nitropack'
import type { H3Event } from 'h3'

export async function $fetchApi<T = unknown>(
    url: NitroFetchRequest,
    options?: NitroFetchOptions<NitroFetchRequest> & {
        skipTokenRefresh?: boolean
    },
    shouldFormatParams: boolean = true
) {
    if (
        !isTokenValid() &&
        process.env.MODE !== 'test' &&
        !options?.skipTokenRefresh
    ) {
        await useAuth().refreshToken()
    }

    const snakeCaseParams = keysToSnake(options?.params)

    const formattedParams = shouldFormatParams
        ? qs.stringify(snakeCaseParams, {
              arrayFormat: 'brackets'
          })
        : ''
    let builtUrl = url

    if (formattedParams) {
        builtUrl += `?${formattedParams}`
    }

    return $fetch<T>(builtUrl, {
        ...options,
        params: shouldFormatParams ? undefined : options?.params,
        body: options?.body ? keysToSnake(options.body) : undefined,
        headers: {
            Authorization: options?.skipTokenRefresh
                ? ''
                : useUserSession().session.value?.tokens?.accessToken || '',
            ...options?.headers
        },
        parseResponse: (response: string): T | undefined | string => {
            if (response) {
                //Special case to allow retrieving html responses
                return response.indexOf('DOCTYPE html') > 0
                    ? response
                    : keysToCamel(JSON.parse(response))
            }

            return undefined
        }
    })
}

export async function $fetchApiRaw<T = unknown>(
    url: NitroFetchRequest,
    options?: NitroFetchOptions<NitroFetchRequest> & {
        skipTokenRefresh?: boolean
    }
) {
    if (
        !isTokenValid() &&
        process.env.MODE !== 'test' &&
        !options?.skipTokenRefresh
    ) {
        await useAuth().refreshToken()
    }

    const snakeCaseParams = keysToSnake(options?.params)

    const formattedParams = qs.stringify(snakeCaseParams, {
        arrayFormat: 'brackets'
    })

    let builtUrl = url

    if (formattedParams) {
        builtUrl += `?${formattedParams}`
    }

    return $fetch.raw<T>(builtUrl, {
        ...options,
        params: undefined,
        body: options?.body ? keysToSnake(options.body) : undefined,
        headers: {
            Authorization: options?.skipTokenRefresh
                ? ''
                : useUserSession().session.value?.tokens?.accessToken || '',
            ...options?.headers
        },
        parseResponse: (response: string): T | undefined => {
            if (response) {
                return keysToCamel(JSON.parse(response))
            }

            return undefined
        }
    })
}

export async function $fetchApiServer<T = unknown>(
    event: H3Event,
    url: NitroFetchRequest,
    options?: NitroFetchOptions<NitroFetchRequest>,
    shouldFormatParams: boolean = true
): Promise<T> {
    const isTokenValid = await isTokenValidServer(event)

    if (!isTokenValid && process.env.MODE !== 'test') {
        await $fetch('/auth/refresh')
    }

    const snakeCaseParams = keysToSnake(options?.params)

    const formattedParams = shouldFormatParams
        ? qs.stringify(snakeCaseParams, {
              arrayFormat: 'brackets'
          })
        : ''
    let builtUrl = url

    if (formattedParams) {
        builtUrl += `?${formattedParams}`
    }

    return $fetch<T>(builtUrl, {
        ...options,
        params: shouldFormatParams ? undefined : options?.params,
        body: options?.body ? keysToSnake(options.body) : undefined,
        headers: {
            Authorization:
                (await getUserSession(event)).tokens?.accessToken || '',
            ...options?.headers
        },
        parseResponse: (response: string): T | undefined | string => {
            if (response) {
                //Special case to allow retrieving html responses
                return response.indexOf('DOCTYPE html') > 0
                    ? response
                    : keysToCamel(JSON.parse(response))
            }

            return undefined
        }
    }) as Promise<T>
}
